---
meta:
  - title: Como usar Tablas IA - IUSPRO Docs
  - description:
      Aprenda a usar Tablas IA para extraer información de muchos documentos y hacer preguntas sobre los resultados.
---

# Como usar Tablas IA

Tablas IA sirve para revisar muchos documentos a la vez y convertir la información importante en una tabla.

En vez de abrir cada archivo manualmente, usted define qué datos quiere extraer y la plataforma llena una fila por cada
documento.

## Para qué sirve

Use Tablas IA cuando tenga varios documentos parecidos y necesite comparar información.

Por ejemplo:

- revisar contratos y extraer monto, plazo, partes, obligaciones o cláusulas importantes
- revisar recursos y extraer resolución impugnada, recurrente, causal, petitoria y citas
- revisar expedientes o documentos sueltos y crear una matriz de datos
- encontrar cuáles documentos tienen una condición específica
- comparar muchos archivos sin tener que leerlos uno por uno

La idea es simple:

- cada documento es una fila
- cada pregunta que quiere responder es una columna
- cada celda contiene el resultado, la razón y la cita usada

## Cuándo conviene usarla

Tablas IA es especialmente útil cuando:

- tiene muchos documentos
- quiere una vista comparativa
- necesita revisar datos repetidos en varios archivos
- quiere encontrar excepciones o diferencias
- necesita guardar el resultado como una tabla de trabajo

Si solo quiere conversar sobre uno o dos documentos, puede usar Auri. Si quiere estructurar muchos documentos en datos,
use Tablas IA.

## Paso 1: Cree una tabla

Entre a **Tablas IA** y cree una nueva tabla.

Puede empezar de dos formas:

- desde cero
- usando una plantilla existente

Una plantilla ya trae columnas preparadas para un tipo de revisión. Esto ayuda cuando va a repetir el mismo análisis
varias veces.

## Paso 2: Suba documentos

Puede agregar documentos con el botón **Agregar documento**.

También puede arrastrar muchos documentos directamente a la pantalla. Esto es útil cuando tiene una carpeta completa con
archivos que quiere revisar juntos.

La plataforma procesa documentos PDF, Word y texto. Cuando termina de procesarlos, cada documento aparece como una fila
en la tabla.

## Paso 3: Cree las columnas

Las columnas le dicen a la IA qué información debe buscar.

Cada columna debe tener:

- **nombre**: lo que verá en la tabla
- **instrucción**: qué debe extraer la IA
- **tipo de dato**: texto, número, fecha, sí/no, dinero u otro tipo disponible

Ejemplos de columnas:

- Recurrente
- Resolución impugnada
- Fecha de notificación
- Monto reclamado
- ¿Existe cláusula arbitral?
- Causal invocada
- Petitoria principal

La instrucción debe ser clara. Por ejemplo:

```
Identifique la resolución que se está impugnando. Incluya tipo de resolución, número y fecha si aparecen.
```

No tiene que escribir instrucciones largas. Lo importante es decir qué quiere encontrar y cómo quiere que se responda.

## Paso 4: Ejecute la extracción

Cuando tenga documentos y columnas, la plataforma analiza cada documento contra cada columna.

Cada resultado puede incluir:

- **resultado**: el dato extraído
- **razón**: por qué se llegó a ese resultado
- **cita**: el texto del documento que respalda la respuesta
- **confianza**: qué tan seguro está el sistema

La cita es importante porque permite verificar rápidamente de dónde salió la respuesta.

## Paso 5: Revise los resultados

Use la tabla como una matriz de revisión.

Puede:

- ordenar visualmente la información
- revisar celda por celda
- volver a intentar resultados que fallaron
- ajustar columnas si una instrucción necesita ser más clara
- agregar más documentos a la misma tabla

Si una columna no está dando buenos resultados, normalmente conviene mejorar la instrucción de esa columna y volver a
ejecutar.

## Usar plantillas

Una plantilla guarda la estructura de columnas para reutilizarla después.

Use plantillas cuando tenga un proceso que se repite, por ejemplo:

- revisión de recursos de casación
- revisión de contratos de arrendamiento
- revisión de poderes
- revisión de documentos laborales
- revisión de expedientes similares

Puede guardar una tabla como plantilla para no tener que volver a crear las mismas columnas cada vez.

## Chat de la tabla

Cada tabla tiene un chat propio.

Este chat conoce:

- los documentos de la tabla
- el contenido procesado de los documentos
- los resultados extraídos en las columnas
- las citas asociadas a los resultados

Puede usarlo para preguntas como:

```
Resume los datos más importantes de la tabla.
```

```
Cuáles documentos tienen cláusula arbitral?
```

```
Dime los 5 documentos con los montos más altos.
```

```
Compara las causales invocadas en todos los recursos.
```

```
Busca documentos donde se mencione una negociación y dime cuáles son los más relevantes.
```

El chat está pensado para analizar la tabla y sus documentos. Por defecto se enfoca en esos archivos. Si necesita
análisis legal más amplio, puede pedirlo expresamente.

## Asociar la tabla a un cliente

Puede asociar una tabla a un cliente desde el selector **Cliente** en la parte superior de la tabla.

Esto no cambia los documentos ni los resultados. Solo sirve para organizar el trabajo.

Cuando una tabla asociada termina de procesarse, aparece como actividad dentro del cliente. Así puede mantener juntos:

- chats del cliente
- documentos del Redactor
- expedientes
- tablas de extracción

## Buenas prácticas

Para obtener mejores resultados:

- use columnas específicas
- evite pedir muchas cosas distintas en una sola columna
- use tipos de dato cuando pueda, por ejemplo fecha, número o dinero
- revise las citas, especialmente en datos importantes
- cree plantillas para trabajos repetidos
- si una respuesta no es buena, ajuste la instrucción de la columna
- use el chat para preguntas comparativas después de tener la tabla llena

## Qué no debería hacer

Evite usar una sola columna para pedir un análisis demasiado amplio.

Por ejemplo, en vez de una columna llamada **Todo el análisis del contrato**, es mejor crear varias columnas:

- Partes
- Monto
- Plazo
- Obligaciones principales
- Terminación anticipada
- Cláusula arbitral

Mientras más clara sea cada columna, más fácil será revisar y comparar los resultados.

## Siguiente lectura

- Lea [cómo usar clientes](/guias/proyectos-como-usar)
- Lea [cómo usar Auri](/guias/auri-como-usar)
- Lea [cómo usar bases de conocimiento](/guias/bases-de-conocimiento-como-usar)
